import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getAircrafts, getCarriers } from "@/queries/landing";
import * as React from "react";
import { UseFormReturn } from "react-hook-form";
import { z } from "zod";

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = React.useState<T>(value);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export const aircraftMetadataFormSchema = z
  .object({
    tail_number: z.string().min(1, "Tail number is required."),
    aircraft_id: z.coerce
      .number({
        required_error: "Aircraft is required.",
        invalid_type_error: "Aircraft ID must be a number.",
      })
      .positive("Aircraft must be selected."),
    general_aviation: z.boolean().default(false).optional(),
    add_new_carrier_mode: z.boolean().default(false),
    carrier_id: z.coerce
      .number({
        invalid_type_error: "Carrier ID must be a number.",
      })
      .positive("Selected carrier ID must be positive.")
      .optional(),
    carrier_name: z.string().trim().optional(),
    carrier_code: z.string().trim().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.general_aviation) {
      if (
        data.carrier_id !== undefined ||
        data.carrier_name ||
        data.carrier_code
      ) {
        // This is more of a safeguard, UI should prevent this.
        // console.warn("Carrier fields should be empty when general_aviation is true.");
      }
    } else {
      if (data.add_new_carrier_mode) {
        // Mode: Add New Carrier Manually
        if (data.carrier_id !== undefined && data.carrier_id !== null) {
          ctx.addIssue({
            path: ["carrier_id"],
            message: "Clear selected carrier when adding a new one manually.",
            code: z.ZodIssueCode.custom,
          });
        }

        if (!data.carrier_name || data.carrier_name.length === 0) {
          ctx.addIssue({
            path: ["carrier_name"],
            message: "New carrier name is required.",
            code: z.ZodIssueCode.custom,
          });
        } else if (data.carrier_name.length < 2) {
          ctx.addIssue({
            path: ["carrier_name"],
            message: "New carrier name must be at least 2 characters.",
            code: z.ZodIssueCode.custom,
          });
        }

        if (!data.carrier_code || data.carrier_code.length === 0) {
          ctx.addIssue({
            path: ["carrier_code"],
            message: "New carrier code is required.",
            code: z.ZodIssueCode.custom,
          });
        } else if (
          data.carrier_code.length < 2 ||
          data.carrier_code.length > 3
        ) {
          ctx.addIssue({
            path: ["carrier_code"],
            message: "New carrier code must be 2 or 3 characters.",
            code: z.ZodIssueCode.custom,
          });
        }
      } else {
        // Mode: Select Existing Carrier
        if (data.carrier_id === undefined || data.carrier_id === null) {
          ctx.addIssue({
            path: ["carrier_id"],
            message: "Carrier selection is required when not General Aviation.",
            code: z.ZodIssueCode.custom,
          });
        }

        if (data.carrier_name && data.carrier_name.length > 0) {
          ctx.addIssue({
            path: ["carrier_name"],
            message:
              "Clear new carrier name when selecting an existing carrier.",
            code: z.ZodIssueCode.custom,
          });
        }
        if (data.carrier_code && data.carrier_code.length > 0) {
          ctx.addIssue({
            path: ["carrier_code"],
            message:
              "Clear new carrier code when selecting an existing carrier.",
            code: z.ZodIssueCode.custom,
          });
        }
      }
    }
  });

export type AircraftMetadataFormValues = z.infer<
  typeof aircraftMetadataFormSchema
>;

interface AircraftMetadataFormProps {
  form: UseFormReturn<AircraftMetadataFormValues>;
  onSubmit: (values: AircraftMetadataFormValues) => void;
  isLoading: boolean;
  submitButtonText?: string;
}

export function AircraftMetadataForm({
  form,
  onSubmit,
  isLoading,
  submitButtonText = "Create",
}: AircraftMetadataFormProps) {
  const { data: aircrafts, isLoading: isLoadingAircrafts } = getAircrafts();
  const { data: carriers, isLoading: isLoadingCarriers } = getCarriers();
  const [aircraftSearchTerm, setAircraftSearchTerm] = React.useState("");
  const [carrierSearchTerm, setCarrierSearchTerm] = React.useState("");

  const debouncedAircraftSearchTerm = useDebounce(aircraftSearchTerm, 300);
  const debouncedCarrierSearchTerm = useDebounce(carrierSearchTerm, 300);

  const initialItemCount = 9;

  const filteredAircrafts = React.useMemo(() => {
    if (!aircrafts) return [];
    if (!debouncedAircraftSearchTerm)
      return aircrafts.slice(0, initialItemCount);
    return aircrafts.filter(
      (ac) =>
        ac.model
          ?.toLowerCase()
          .includes(debouncedAircraftSearchTerm.toLowerCase()) ||
        ac.code_iata
          ?.toLowerCase()
          .includes(debouncedAircraftSearchTerm.toLowerCase()) ||
        ac.code_icao
          ?.toLowerCase()
          .includes(debouncedAircraftSearchTerm.toLowerCase())
    );
  }, [aircrafts, debouncedAircraftSearchTerm, initialItemCount]);

  const filteredCarriers = React.useMemo(() => {
    if (!carriers) return [];
    if (!debouncedCarrierSearchTerm) return carriers.slice(0, initialItemCount);
    return carriers.filter(
      (cr) =>
        cr.name
          ?.toLowerCase()
          .includes(debouncedCarrierSearchTerm.toLowerCase()) ||
        cr.code
          ?.toLowerCase()
          .includes(debouncedCarrierSearchTerm.toLowerCase())
    );
  }, [carriers, debouncedCarrierSearchTerm, initialItemCount]);

  const watchGeneralAviation = form.watch("general_aviation");

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="tail_number"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Tail Number</FormLabel>
              <FormControl>
                <Input placeholder="e.g., N12345" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="aircraft_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Aircraft</FormLabel>
              <Select
                onValueChange={(value) =>
                  field.onChange(value ? parseInt(value) : undefined)
                }
                value={field.value?.toString()}
                disabled={isLoadingAircrafts}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select an aircraft" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent onCloseAutoFocus={(e) => e.preventDefault()}>
                  <div
                    className="p-2"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Input
                      placeholder="Search aircraft..."
                      value={aircraftSearchTerm}
                      onChange={(e) => setAircraftSearchTerm(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      className="mb-2"
                    />
                  </div>
                  {isLoadingAircrafts && !filteredAircrafts?.length && (
                    <SelectItem value="loading" disabled>
                      Loading aircraft...
                    </SelectItem>
                  )}
                  {filteredAircrafts?.map((ac) => (
                    <SelectItem key={ac.id} value={ac.id.toString()}>
                      {ac.model} ({ac.code_iata || ac.code_icao || "N/A"})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {!watchGeneralAviation && (
          <>
            {form.watch("add_new_carrier_mode") ? (
              <>
                <FormField
                  control={form.control}
                  name="carrier_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>New Carrier Name</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter new carrier name"
                          {...field}
                          value={field.value ?? ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="carrier_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>New Carrier Code</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter IATA or ICAO code"
                          {...field}
                          value={field.value ?? ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            ) : (
              <FormField
                control={form.control}
                name="carrier_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Carrier</FormLabel>
                    <Select
                      onValueChange={(value) =>
                        field.onChange(value ? parseInt(value, 10) : undefined)
                      }
                      value={field.value?.toString()}
                      disabled={isLoadingCarriers}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a carrier" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent
                        onCloseAutoFocus={(e) => e.preventDefault()}
                      >
                        <div
                          className="p-2"
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Input
                            placeholder="Search carrier..."
                            value={carrierSearchTerm}
                            onChange={(e) =>
                              setCarrierSearchTerm(e.target.value)
                            }
                            onKeyDown={(e) => e.stopPropagation()}
                            className="mb-2"
                          />
                        </div>
                        {isLoadingCarriers && !filteredCarriers?.length && (
                          <SelectItem value="loading" disabled>
                            Loading carriers...
                          </SelectItem>
                        )}
                        {filteredCarriers?.map((cr) => (
                          <SelectItem key={cr.id} value={cr.id.toString()}>
                            {cr.name} ({cr.code})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="add_new_carrier_mode"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center space-x-3 space-y-[-1px]">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(checkedState) => {
                        const isNowAddingNew = !!checkedState;
                        field.onChange(isNowAddingNew);
                        if (isNowAddingNew) {
                          form.resetField("carrier_id", {
                            defaultValue: undefined,
                          });
                          form.clearErrors("carrier_id");
                        } else {
                          form.resetField("carrier_name", { defaultValue: "" });
                          form.resetField("carrier_code", { defaultValue: "" });
                          form.clearErrors(["carrier_name", "carrier_code"]);
                        }
                      }}
                      id="addNewCarrierToggle"
                    />
                  </FormControl>
                  <div>
                    <FormLabel
                      htmlFor="addNewCarrierToggle"
                      className="text-sm font-medium"
                    >
                      Add new carrier manually
                    </FormLabel>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />
          </>
        )}

        <FormField
          control={form.control}
          name="general_aviation"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center space-x-3 space-y-[-1px]">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={(checkedState) => {
                    const isNowGeneralAviation = !!checkedState;
                    field.onChange(isNowGeneralAviation);
                    if (isNowGeneralAviation) {
                      form.resetField("carrier_id", {
                        defaultValue: undefined,
                      });
                      form.resetField("carrier_name", { defaultValue: "" });
                      form.resetField("carrier_code", { defaultValue: "" });
                      form.resetField("add_new_carrier_mode", {
                        defaultValue: false,
                      });
                      // Clear errors for carrier fields
                      form.clearErrors([
                        "carrier_id",
                        "carrier_name",
                        "carrier_code",
                      ]);
                    }
                  }}
                  id="generalAviationToggle"
                />
              </FormControl>
              <div>
                <FormLabel
                  htmlFor="generalAviationToggle"
                  className="text-sm font-medium"
                >
                  General Aviation
                </FormLabel>
                <FormMessage />
              </div>
            </FormItem>
          )}
        />

        <p className="text-sm text-muted-foreground">
          Creating aircraft metadata will also update corresponding landing fee
          records with the new aircraft and carrier information where not
          already present. This action is irreversible.
        </p>

        <Button type="submit" disabled={isLoading} className="w-full">
          {isLoading ? "Saving..." : submitButtonText}
        </Button>
      </form>
    </Form>
  );
}
